import { type ReactNode, useEffect, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

interface ConfirmDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: ReactNode;
  /** When set, the confirm button stays disabled until the user types this string. */
  confirmText?: string;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
}

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  title,
  message,
  confirmText,
  confirmLabel = 'Confirm',
  destructive = false,
  busy = false,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const canConfirm =
    !busy && (!confirmText || typed.trim() === confirmText.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={() => !busy && onCancel()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal
      >
        <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2.5 min-w-0">
            {destructive && (
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={16} className="text-amber-600" />
              </div>
            )}
            <h3 className="text-base font-semibold text-navy-900 truncate">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-40"
            aria-label="Cancel"
          >
            <X size={16} />
          </button>
        </header>
        <div className="px-5 py-4 text-sm text-slate-600 space-y-3">
          <div>{message}</div>
          {confirmText && (
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Type{' '}
                <span className="font-mono font-semibold text-navy-900">
                  {confirmText}
                </span>{' '}
                to confirm
              </label>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={busy}
                autoFocus
                className="w-full px-2.5 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/30 focus:border-teal-500 font-mono"
              />
            </div>
          )}
        </div>
        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={!canConfirm}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg shadow-sm disabled:opacity-40 disabled:cursor-not-allowed',
              destructive
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-teal-600 text-white hover:bg-teal-700',
            )}
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}
